import { useState } from 'react';
import type { KeyboardEventHandler } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface MainAddressSelectorProps {
    value: string;
    customValue: string;
    options: string[];
    onChange: (value: string) => void;
    onCustomChange: (value: string) => void;
    showLabel?: boolean;
    disabled?: boolean;
    /** Autofocus the Select — only pass true when the TextField is not shown, to avoid focus conflicts. */
    autoFocusSelect?: boolean;
    autoFocusCustomField?: boolean;
    onCustomKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

export function MainAddressSelector({
    value,
    customValue,
    options,
    onChange,
    onCustomChange,
    showLabel = true,
    disabled,
    autoFocusSelect,
    autoFocusCustomField,
    onCustomKeyDown,
}: MainAddressSelectorProps) {
    const { t } = useTranslation();
    const [overwriteOpen, setOverwriteOpen] = useState(false);
    const [overwritePending, setOverwritePending] = useState('');
    const [overwriteLost, setOverwriteLost] = useState('');

    function handleDropdownChange(next: string) {
        if (value === '__other__' && customValue.trim() && next !== '__other__') {
            setOverwriteLost(customValue.trim());
            setOverwritePending(next);
            setOverwriteOpen(true);
            return;
        }
        onChange(next);
        if (next !== '__other__') onCustomChange('');
    }

    function handleConfirmOverwrite() {
        onChange(overwritePending);
        onCustomChange('');
        setOverwriteOpen(false);
    }

    return (
        <>
            <FormControl size="small" fullWidth required>
                {showLabel && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        {t('Main address')} *
                    </Typography>
                )}
                <Select
                    displayEmpty
                    value={value}
                    disabled={disabled}
                    autoFocus={autoFocusSelect}
                    onChange={(e) => handleDropdownChange(e.target.value)}
                >
                    <MenuItem value="" disabled>
                        <em>{t('Select main address')}</em>
                    </MenuItem>
                    {options.map((addr) => (
                        <MenuItem key={addr} value={addr}>
                            {addr}
                        </MenuItem>
                    ))}
                    <MenuItem value="__other__">{t('Other')}</MenuItem>
                </Select>
            </FormControl>
            {value === '__other__' && (
                <TextField
                    label={t('Add custom main address')}
                    size="small"
                    fullWidth
                    value={customValue}
                    disabled={disabled}
                    autoFocus={autoFocusCustomField}
                    onChange={(e) => onCustomChange(e.target.value)}
                    onKeyDown={onCustomKeyDown}
                />
            )}
            <Dialog open={overwriteOpen} onClose={() => setOverwriteOpen(false)}>
                <DialogTitle>{t('Change main address?')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('You are about to set the following address as the main address:')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
                        {overwritePending}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('The previous address will be lost:')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {overwriteLost}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOverwriteOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={handleConfirmOverwrite}>
                        {t('Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
